'use client';

import { BookingResponse, User } from '@api/models';
import React, { createContext, useContext, useMemo } from 'react';

import { useBookingFlow } from '@website/hooks/useBookingFlow';
import { useSlotAnalysis } from '@website/hooks/useSlotAnalysis';
import { useStepNavigation } from '@website/hooks/useStepNavigation';
import { ActivityBookingData } from '@website/services/activities.service';
import { BookingData } from '@website/services/bookings.service';

interface BookingContextType {
  // Core hooks
  bookingFlow: ReturnType<typeof useBookingFlow>;
  navigation: ReturnType<typeof useStepNavigation>;

  // Transformed data
  activity: ReturnType<typeof useSlotAnalysis>['transformedActivity'];

  // Configuration. These are the ACTIVITY-LEVEL location fallback used only when
  // the selected slot carries no resolved location (legacy slots). The per-slot
  // location is threaded through the booking form (`slotLocation`) and read by the
  // wizard steps (AddressStep / ReviewStep / BookingSummaryWrapper).
  isAtHomeActivity: boolean;
  activityCoordinates: {
    latitude?: number;
    longitude?: number;
    homeRadius?: number;
  };
  slots: any[];
  locale: string;
  activityId: string;
  user: User;

  // Edit mode
  bookingData?: BookingData | null;
  rawBookingData?: BookingResponse | null;
  isEditMode: boolean;

  // Slot analysis
  hasOnlyOneBookingType: boolean;

  // Payment-based edit permissions
  paymentStatus?: string;
  canEditSlot: boolean;
  canEditAddress: boolean;
  isPaymentFlow: boolean;
}

const BookingContext = createContext<BookingContextType | null>(null);

interface BookingProviderProps {
  children: React.ReactNode;
  activityData: ActivityBookingData;
  user: User;
  locale: string;
  activityId: string;
  bookingData?: BookingData | null;
  rawBookingData?: BookingResponse | null;
  initialStep?: string;
}

export function BookingProvider({
  children,
  activityData,
  user,
  locale,
  activityId,
  bookingData,
  rawBookingData,
  initialStep
}: BookingProviderProps) {
  const { transformedActivity, getInitialBookingType, hasOnlyOneBookingType } = useSlotAnalysis(activityData);

  const isEditMode = Boolean(bookingData);
  const paymentStatus = rawBookingData?.paymentStatus;

  // Compute edit permissions based on payment status
  // Rules:
  // - PAYMENT_RECEIVED (paid): No slot changes, only address changes for at-home activities
  // - PENDING_PAYMENT: All changes allowed (will pay for new slot)
  // - PROCESSING_PAYMENT: No changes allowed (payment in flight)
  // - CASH/FREE: All changes allowed
  // - REFUNDED/EXPIRED/FAILED/DISPUTED/PAYOUT_READY/PAID_OUT: No changes allowed
  const canEditSlot = useMemo(() => {
    if (!isEditMode) return true; // Not editing, no restrictions
    if (!paymentStatus) return true; // No payment status, allow edits

    const terminalStatuses = ['REFUNDED', 'EXPIRED', 'FAILED', 'DISPUTED', 'PAYOUT_READY', 'PAID_OUT'];
    if (terminalStatuses.includes(paymentStatus)) return false;

    if (paymentStatus === 'PAYMENT_RECEIVED') return false;
    if (paymentStatus === 'PROCESSING_PAYMENT') return false;

    // PENDING_PAYMENT, CASH, FREE all allow slot edits
    return true;
  }, [isEditMode, paymentStatus]);

  const canEditAddress = useMemo(() => {
    if (!isEditMode) return true;
    if (!activityData.isAtHome) return false; // Only at-home activities have addresses

    if (!paymentStatus) return true;

    const terminalStatuses = ['REFUNDED', 'EXPIRED', 'FAILED'];
    if (terminalStatuses.includes(paymentStatus)) return false;

    // All non-terminal states allow address edits for at-home activities
    return true;
  }, [isEditMode, paymentStatus, activityData.isAtHome]);

  // isPaymentFlow: true when editing a PENDING_PAYMENT booking
  // In this case, after any slot changes, we should go to payment (not update confirmation)
  const isPaymentFlow = useMemo(() => {
    return isEditMode && paymentStatus === 'PENDING_PAYMENT';
  }, [isEditMode, paymentStatus]);

  const bookingFlow = useBookingFlow(activityData.isAtHome || false, initialStep);

  const navigation = useStepNavigation({
    currentStep: bookingFlow.currentStep,
    previousStep: bookingFlow.previousStep
  });

  // Initialize selected slot based on analysis
  React.useEffect(() => {
    bookingFlow.setSelectedSlot(getInitialBookingType);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bookingFlow.setSelectedSlot is a stable callback from useBookingFlow hook
  }, [getInitialBookingType, bookingFlow.setSelectedSlot]);

  // Memoize slots array to prevent new references on every render
  const slots = useMemo(() => {
    return activityData.slots || [];
  }, [activityData.slots]);

  // Memoize context value to prevent recreation on every render
  const contextValue: BookingContextType = useMemo(
    () => ({
      bookingFlow,
      navigation,
      activity: transformedActivity,
      isAtHomeActivity: activityData.isAtHome || false,
      activityCoordinates: {
        latitude: activityData.location?.coordinates?.latitude ?? undefined,
        longitude: activityData.location?.coordinates?.longitude ?? undefined,
        homeRadius: activityData.homeRadius
      },
      slots,
      locale,
      activityId,
      user,
      bookingData,
      rawBookingData,
      isEditMode,
      hasOnlyOneBookingType,
      paymentStatus,
      canEditSlot,
      canEditAddress,
      isPaymentFlow
    }),
    [
      bookingFlow,
      navigation,
      transformedActivity,
      activityData.isAtHome,
      activityData.location?.coordinates?.latitude,
      activityData.location?.coordinates?.longitude,
      activityData.homeRadius,
      slots,
      locale,
      activityId,
      user,
      bookingData,
      rawBookingData,
      isEditMode,
      hasOnlyOneBookingType,
      paymentStatus,
      canEditSlot,
      canEditAddress,
      isPaymentFlow
    ]
  );

  return <BookingContext.Provider value={contextValue}>{children}</BookingContext.Provider>;
}

export const useBookingContext = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBookingContext must be used within a BookingProvider');
  }
  return context;
};
